/** Cele două piese de formular pe care le împart Detaliile terenului și Tema
 *  proiectului: eticheta unui câmp și chip-ul care spune DE UNDE vine valoarea.
 *
 *  Stăteau în `Localizare.tsx` pe vremea când localizarea și tema erau un
 *  singur ecran. De când sunt doi pași, o pagină care importă componente de UI
 *  dintr-o altă pagină e o dependență care nu spune nimic despre ce împart
 *  cele două — așa că împărțitul stă aici, cu numele lui.
 */
import type { ReactNode } from "react";

import { useT } from "../i18n";
import type { Cheie } from "../i18n";
import type { Localizare as Loc } from "./dosarclient";

const SCURT: Record<string, Cheie> = {
  certificat_urbanism: "loc.scurt.certificat_urbanism",
  cadastru: "loc.scurt.cadastru",
  plan_topografic: "loc.scurt.plan_topografic",
  // Adresa poate veni și din studiul geotehnic, când dosarul n-are certificat.
  // E o sursă mai slabă decât un act, deci merită numită: cine vede „din geo"
  // știe că adresa e de pe coperta unui raport, nu dintr-un act.
  studiu_geotehnic: "loc.scurt.studiu_geotehnic",
  solutie_arhitecturala: "loc.scurt.solutie_arhitecturala",
  solutie_structurala: "loc.scurt.solutie_structurala",
  calculat: "loc.scurt.calculat",
};

// Aceleași surse, scrise întreg — pentru cartonașul de adresă, unde macheta le
// numește complet („din Certificatul de urbanism").
const LUNG: Record<string, Cheie> = {
  certificat_urbanism: "loc.lung.certificat_urbanism",
  cadastru: "loc.lung.cadastru",
  plan_topografic: "loc.lung.plan_topografic",
  studiu_geotehnic: "loc.lung.studiu_geotehnic",
  solutie_arhitecturala: "loc.lung.solutie_arhitecturala",
  solutie_structurala: "loc.lung.solutie_structurala",
  calculat: "loc.lung.calculat",
};

// CHIP-UL S-A ÎNTORS LÂNGĂ ETICHETĂ. Stătea ÎN câmp, la dreapta (`CuChip`),
// fiindcă „REGIM ÎNĂLȚIME" plus un chip nu încăpeau într-o coloană de 150 px.
// De când regimul se alege din două liste, eticheta lui ține un rând întreg de
// 430 px — deci chip-ul încape lângă ea, iar suprapunerea peste săgeata
// selectului nu mai e nevoie nicăieri.
export function Camp({ eticheta, obligatoriu, chip, children }: {
  eticheta: Cheie; obligatoriu?: boolean; chip?: ReactNode;
  children: ReactNode;
}) {
  const t = useT();
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5, minWidth: 0 }}>
      {/* Chip-ul stă LÂNGĂ etichetă, nu în ea: `.lbl` e majuscule cu spațiere
          largă, iar un nume de fișier trecut prin ea devine ilizibil. */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
        <span className="lbl">{t(eticheta)}{obligatoriu ? " *" : ""}</span>
        {chip}
      </div>
      {children}
    </div>
  );
}

// De unde vine valoarea. Macheta are chip-ul „din CU" lângă regimul de
// înălțime; e generalizat aici, fiindcă întrebarea „de unde știi asta?" se pune
// despre fiecare câmp, nu doar despre acela.
// `loc` e `null` până se citește ceva din acte — ecranul se poate deschide
// înaintea oricărei analize. Tipul o spune; un `as Loc` la apelant ar ascunde-o
// exact până la prima randare.
// `parinte` e pentru câmpurile dintr-un dicționar (`regim_urbanistic.pot_max`):
// acolo corecția omului se raportează cu cheia punctată, iar fără ea un POT
// tastat de om ar continua să scrie „din CU" deasupra.
export function Sursa({ loc, camp, intreg, parinte }: {
  loc: Loc | null; camp: string; intreg?: boolean; parinte?: string;
}) {
  const t = useT();
  if (!loc) return null;
  if (loc.manual?.includes(parinte ? `${parinte}.${camp}` : camp)) {
    return (
      <span className="chip c-met" style={{ fontSize: 10 }}>
        {t("loc.schimbat_de_tine")}
      </span>
    );
  }
  const p = loc.provenienta?.[camp];
  if (!p) return null;
  // Pe chip intră FELUL actului, nu numele fișierului — care nu încape lângă o
  // etichetă și, tăiat la jumătate, nu mai răspunde la nimic. Numele întreg stă
  // în `title`. `intreg` e pentru cartonașul de adresă, unde macheta scrie
  // numele complet al actului fiindcă acolo e loc.
  const cat = loc.provenienta_cat?.[camp] || "";
  const text = intreg
    ? (LUNG[cat] ? t(LUNG[cat]) : t("loc.din_acte"))
    : t("loc.din_scurt", { sursa: SCURT[cat] ? t(SCURT[cat])
                                             : t("loc.din_acte_scurt") });
  return <span className="chip c-mut" style={{ fontSize: 10, flex: "none" }}
               title={t("loc.din_fisier", { fisier: p })}>{text}</span>;
}

